import { type Container, type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo, useRef } from "react";
import { useConstantScreenSize } from "../hooks/useConstantScreenSize";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import type { LayerId, MapItemHandler, MarkerMapObject } from "../types";
import OutlinedBitmapText from "./OutlinedBitmapText";

const MARKER_WIDTH_FACTOR = 0.5;
const LABEL_OFFSET_FACTOR = 0.05;
const DOT_RADIUS_FACTOR = 0.15;
const REFERENCE_MARKER_HEIGHT_M = 0.714;
const REFERENCE_MARKER_HEIGHT_PX = 21;
const MARKER_LABEL_FONT_SIZE_PX = 20;
const MARKER_SCALE_LIMITS = {
    minScale: 0.2,
    maxScale: 0.6,
};

interface MarkerObjectProps {
    item: MarkerMapObject;
    layerId: LayerId;
    detected?: boolean;
    onItemClick?: MapItemHandler<MarkerMapObject>;
    onItemHover?: MapItemHandler<MarkerMapObject>;
    onItemLeave?: MapItemHandler<MarkerMapObject>;
}

const getAlpha = (valid: boolean): number => (valid ? 1 : 0.3);

const getLabelOffset = (yaw: number, labelOffset: number): number => {
    const adjustedRotation = ((Number(yaw) + 180) % 360) - 180;
    const sign = adjustedRotation <= 0 ? 1 : -1;
    return sign * labelOffset;
};

function useMarkerGeometry(item: MarkerMapObject) {
    const { coordinates } = useScene();
    const markerGeometry = useMemo(() => {
        const visualHeight =
            REFERENCE_MARKER_HEIGHT_PX *
            (item.height / REFERENCE_MARKER_HEIGHT_M);
        const halfVisualWidth = visualHeight * MARKER_WIDTH_FACTOR;
        const labelOffsetY = -getLabelOffset(
            item.yaw,
            visualHeight * LABEL_OFFSET_FACTOR,
        );
        const center = coordinates.worldToScene({ x: item.x, y: item.y });

        return {
            x: center.x,
            y: center.y,
            labelOffsetY,
            visualHeight,
            halfVisualWidth,
            dotRadius: visualHeight * DOT_RADIUS_FACTOR,
            strokeWidth: visualHeight * 0.02,
            hoverStrokeWidth: visualHeight * 0.06,
            rotation: ((Number(item.yaw) - 90) % 360) * (-Math.PI / 180),
        };
    }, [item.x, item.y, item.height, item.yaw, coordinates]);
    const bounds = useMemo(() => {
        const size = markerGeometry.visualHeight * 2;
        return new Rectangle(-size / 2, -size / 2, size, size);
    }, [markerGeometry]);

    return { markerGeometry, bounds };
}

const MarkerObject = ({
    item,
    layerId,
    detected = false,
    onItemClick,
    onItemHover,
    onItemLeave,
}: MarkerObjectProps) => {
    const containerRef = useRef<Container | null>(null);
    const graphicRef = useRef<Graphics | null>(null);
    const { markerGeometry, bounds } = useMarkerGeometry(item);

    const hitArea = useMemo(
        () =>
            new Rectangle(
                -markerGeometry.halfVisualWidth,
                -markerGeometry.visualHeight,
                markerGeometry.halfVisualWidth * 2,
                markerGeometry.visualHeight + markerGeometry.halfVisualWidth,
            ),
        [markerGeometry],
    );

    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });

    useConstantScreenSize(containerRef, MARKER_SCALE_LIMITS);

    const draw = useCallback(
        (graphics: Graphics) => {
            graphicRef.current = graphics;
            const {
                visualHeight,
                halfVisualWidth,
                dotRadius,
                strokeWidth,
                hoverStrokeWidth,
            } = markerGeometry;
            const alpha = getAlpha(item.valid);
            const factor = 0.6;

            graphics.clear();

            graphics.setStrokeStyle({
                color: 0x0000ff,
                width: strokeWidth,
                alpha,
            });
            graphics.setFillStyle({ color: 0x0000ff, alpha });
            graphics.poly([
                -halfVisualWidth,
                halfVisualWidth,
                0,
                -visualHeight,
                halfVisualWidth,
                halfVisualWidth,
            ]);
            graphics.fill();
            graphics.stroke();

            if (detected) {
                graphics.setFillStyle({ color: 0x00ff00, alpha });
                graphics.poly([
                    -halfVisualWidth * factor,
                    halfVisualWidth * factor,
                    0,
                    -visualHeight * factor,
                    halfVisualWidth * factor,
                    halfVisualWidth * factor,
                ]);
                graphics.fill();
            }

            graphics.setStrokeStyle({
                color: 0xff0000,
                width: strokeWidth,
                alpha,
            });
            graphics.setFillStyle({ color: 0xff0000, alpha });
            graphics.circle(0, 0, dotRadius);
            graphics.fill();
            graphics.stroke();

            if (interaction.hovered) {
                graphics.setStrokeStyle({
                    color: 0xff0000,
                    width: hoverStrokeWidth,
                    alpha: 1,
                });
                graphics.poly([
                    -halfVisualWidth,
                    halfVisualWidth,
                    0,
                    -visualHeight,
                    halfVisualWidth,
                    halfVisualWidth,
                ]);
                graphics.stroke();
            }
        },
        [markerGeometry, item.valid, detected, interaction.hovered],
    );

    return (
        <pixiContainer
            ref={containerRef}
            x={markerGeometry.x}
            y={markerGeometry.y}
            label="marker-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={bounds}
        >
            <pixiGraphics
                ref={graphicRef}
                rotation={markerGeometry.rotation}
                eventMode={interaction.eventMode}
                cursor={interaction.cursor}
                hitArea={hitArea}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={draw}
            />
        </pixiContainer>
    );
};

export const MarkerLabel = memo(({ item }: { item: MarkerMapObject }) => {
    const { labelFontReady } = useScene();
    const { markerGeometry } = useMarkerGeometry(item);

    if (!item.label || !labelFontReady) return null;

    const labelFontSize = MARKER_LABEL_FONT_SIZE_PX;
    const labelWidth = Math.max(
        labelFontSize,
        item.label.length * labelFontSize,
    );
    const labelHeight = labelFontSize;
    const labelTop =
        markerGeometry.labelOffsetY < 0
            ? markerGeometry.labelOffsetY - labelHeight
            : markerGeometry.labelOffsetY;
    const labelBounds = new Rectangle(
        -labelWidth / 2,
        labelTop,
        labelWidth,
        labelHeight,
    );

    return (
        <LoadedMarkerLabel
            text={item.label}
            alpha={getAlpha(item.valid)}
            markerGeometry={markerGeometry}
            labelFontSize={labelFontSize}
            labelBounds={labelBounds}
        />
    );
});

const LoadedMarkerLabel = ({
    text,
    alpha,
    markerGeometry,
    labelFontSize,
    labelBounds,
}: {
    text: string;
    alpha: number;
    markerGeometry: ReturnType<typeof useMarkerGeometry>["markerGeometry"];
    labelFontSize: number;
    labelBounds: Rectangle;
}) => {
    const containerRef = useRef<Container | null>(null);

    useConstantScreenSize(containerRef, MARKER_SCALE_LIMITS);

    return (
        <pixiContainer
            ref={containerRef}
            x={markerGeometry.x}
            y={markerGeometry.y}
            label="marker-label-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={labelBounds}
            alpha={alpha}
            eventMode="none"
        >
            <OutlinedBitmapText
                text={text}
                fontSize={labelFontSize}
                outlineOffset={1.5}
                anchor={{ x: 0.5, y: markerGeometry.labelOffsetY < 0 ? 1 : 0 }}
                y={markerGeometry.labelOffsetY}
            />
        </pixiContainer>
    );
};

MarkerLabel.displayName = "MarkerLabel";

export const MarkerSelectionOverlay = memo(
    ({ item }: { item: MarkerMapObject }) => {
        const containerRef = useRef<Container | null>(null);
        const { markerGeometry } = useMarkerGeometry(item);
        const draw = useCallback(
            (graphics: Graphics) => {
                const { visualHeight, halfVisualWidth, hoverStrokeWidth } =
                    markerGeometry;

                graphics.clear();
                graphics.setStrokeStyle({
                    color: 0xff0000,
                    width: hoverStrokeWidth,
                    alpha: 1,
                });
                graphics.poly([
                    -halfVisualWidth,
                    halfVisualWidth,
                    0,
                    -visualHeight,
                    halfVisualWidth,
                    halfVisualWidth,
                ]);
                graphics.stroke();
            },
            [markerGeometry],
        );

        useConstantScreenSize(containerRef, MARKER_SCALE_LIMITS);

        return (
            <pixiContainer
                ref={containerRef}
                x={markerGeometry.x}
                y={markerGeometry.y}
                eventMode="none"
            >
                <pixiGraphics
                    rotation={markerGeometry.rotation}
                    eventMode="none"
                    draw={draw}
                />
            </pixiContainer>
        );
    },
);

MarkerSelectionOverlay.displayName = "MarkerSelectionOverlay";

export default memo(MarkerObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.detected === next.detected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
